// ============================================================
//  AUTH CONTEXT — ADMIN DASHBOARD
//  Path: src/context/AuthContext.tsx
//
//  CHANGE (authenticator-app MFA pass): adds TOTP second-factor
//  support on top of the existing email/password flow. Two separate
//  things live here now:
//
//    1. SIGN-IN CHALLENGE — login() can no longer assume a successful
//       signInWithEmailAndPassword means the admin is signed in.
//       Firebase throws 'auth/multi-factor-auth-required' instead
//       when the account has TOTP enrolled; that error carries a
//       MultiFactorResolver, which this context stores and exposes
//       via mfaResolver/mfaRequired. LoginPage shows a second form
//       (the 6-digit code) and calls completeMfaSignIn(code) to
//       finish. cancelMfaSignIn() abandons the attempt (e.g. the
//       admin clicks "back to login").
//
//    2. ENROLLMENT — beginTotpEnrollment()/completeTotpEnrollment()
//       let an ALREADY signed-in admin add an authenticator app to
//       their own account, for SettingsPage's "Two-Factor
//       Authentication" section. mfaEnrolled reports whether the
//       CURRENT firebaseUser already has a TOTP factor, straight off
//       the Firebase user object — no separate backend call needed,
//       since Firebase owns this state, not our own database.
//
//  Requires firebase JS SDK v10.7.0+ (TotpMultiFactorGenerator).
//  Google Identity Platform (not bare Firebase Auth) must be the
//  project's auth provider and TOTP MFA must be turned on for the
//  project — the console has no TOTP toggle; it's enabled through
//  the Identity Toolkit Admin API (see the "Enabling TOTP MFA" doc).
//  Without that, enrollment fails with auth/operation-not-allowed,
//  which the Settings UI surfaces as a plain message.
//
//  ADMIN_MFA_ENFORCED (backend) starts in warn-mode — nobody is
//  actually locked out by not having MFA set up yet. This context
//  doesn't need to know that flag's value at all: it just makes
//  enrollment and the sign-in challenge available. The moment
//  ADMIN_MFA_ENFORCED flips to true, admins without a TOTP factor
//  keep signing in fine (MFA is a property of THEIR account, set by
//  enrolling), but start getting 401s from the document-view endpoint
//  and other requireMfa-gated routes.
//
//  FIX (review pass):
//  - Removed the MFA-debugging console.logs. One printed the whole
//    GET /api/auth/me response (admin's id, email, roles) on every page
//    load; others printed the login email and Firebase UID.
//  - Stale profile loads: if the admin signed out (or another account
//    signed in) while a profile fetch was still in flight, the late
//    response could set `user` back to the previous admin. Results are
//    now applied only if they're for the account still signed in.
//  - A deactivated admin (isActive false) was let into the dashboard,
//    where every request then failed with "Account deactivated". They
//    are now signed out at login with LoginPage's existing "account has
//    been disabled" message.
//  - A malformed /me response (no roles array) threw a TypeError that
//    showed as a generic failure; it's now a clear "not admin" refusal.
//  - After enrolling an authenticator app, mfaEnrolled could stay false
//    until something else re-rendered: reload() updates the same user
//    object, and setting state to the same object doesn't re-render.
//  - The pending TOTP secret is cleared on sign-out, so a half-finished
//    setup can't carry over to the next account on this browser.
// ============================================================

import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  MultiFactorResolver,
  MultiFactorError,
  TotpSecret,
  User as FirebaseUser,
} from 'firebase/auth'
import { auth } from '../config/firebase'
import { getMeApi } from '../api/auth.api'

interface AdminUser {
  id:        string
  firstName: string
  lastName:  string
  email:     string
  roles:     string[]
  isActive:  boolean
  createdAt: string
}

interface AuthContextType {
  firebaseUser: FirebaseUser | null
  user:         AdminUser | null
  isLoading:    boolean
  isAdmin:      boolean
  login:        (email: string, password: string) => Promise<void>
  logout:       () => Promise<void>
  // ── MFA sign-in challenge ──────────────────────────────────
  // True between login() hitting 'auth/multi-factor-auth-required'
  // and completeMfaSignIn/cancelMfaSignIn resolving it. LoginPage
  // renders the code-entry form only while this is true.
  mfaRequired:      boolean
  completeMfaSignIn: (code: string) => Promise<void>
  cancelMfaSignIn:   () => void
  // ── TOTP enrollment (for an already signed-in admin) ───────
  // Whether the CURRENT firebaseUser already has a TOTP factor —
  // read straight from the Firebase user object.
  mfaEnrolled:            boolean
  beginTotpEnrollment:    () => Promise<{ qrCodeUrl: string; secretKey: string }>
  completeTotpEnrollment: (code: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

const ADMIN_ROLES = ['super_admin', 'admin', 'customer_service']

// Thrown by login() when Firebase auth succeeds but the account
// has no admin-facing role, or when the profile fetch itself
// fails. LoginPage checks err.code to show a specific message
// instead of the generic Firebase-auth-error fallback.
class AdminLoginError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.code = code
  }
}

// Shared by login() and the onAuthStateChanged listener so both
// paths apply the exact same role check with the exact same
// error handling — no drift between "log in right now" and
// "restore session on page load".
const fetchAdminProfile = async (fbUser: FirebaseUser): Promise<AdminUser> => {
  // Force token refresh to ensure the interceptor has a valid token
  await fbUser.getIdToken(true)
  const response = await getMeApi()
  const dbUser: AdminUser | undefined = response?.data?.data

  const roles = Array.isArray(dbUser?.roles) ? dbUser!.roles : []
  const hasAdminRole = roles.some((r: string) => ADMIN_ROLES.includes(r))
  if (!dbUser || !hasAdminRole) {
    await firebaseSignOut(auth)
    throw new AdminLoginError('not-admin', 'This account does not have admin access.')
  }
  // Every admin route refuses a deactivated account (role.middleware:
  // 403 "Account deactivated"), so letting one in only leads to a
  // dashboard where nothing loads. Same code as Firebase's own
  // disabled-account error, so LoginPage's existing message applies.
  if (dbUser.isActive === false) {
    await firebaseSignOut(auth)
    throw new AdminLoginError('auth/user-disabled', 'This account has been disabled.')
  }
  return dbUser
}

// True if `fbUser` is still the account signed in right now — used to
// drop profile results that arrive after a sign-out or account switch.
const isStillSignedIn = (fbUser: FirebaseUser) => auth.currentUser?.uid === fbUser.uid

// Firebase's own TOTP MFA error code for "this account requires a
// second factor" — thrown by signInWithEmailAndPassword, not a
// generic auth error, so it's checked by code rather than caught as
// AdminLoginError.
const MFA_REQUIRED_CODE = 'auth/multi-factor-auth-required'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [user, setUser]                 = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading]       = useState(true)

  // Not exposed directly — LoginPage only ever needs to know
  // mfaRequired (whether to show the code form) and call
  // completeMfaSignIn/cancelMfaSignIn. The resolver itself carries
  // the pending sign-in attempt and is only meaningful internally.
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null)

  // Bumped after enrollment so mfaEnrolled is recomputed — see
  // completeTotpEnrollment.
  const [, setMfaStateVersion] = useState(0)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser)

      if (fbUser) {
        try {
          const dbUser = await fetchAdminProfile(fbUser)
          // Signed out / switched account while this was loading —
          // the newer auth-state event owns `user` now.
          if (!isStillSignedIn(fbUser)) return
          setUser(dbUser)
        } catch (err) {
          // Previously swallowed silently — logged now so a
          // broken /api/auth/me call or role rejection actually
          // shows up somewhere instead of just leaving the user
          // stuck on the login screen with no clue why.
          console.error('Admin session check failed:', err)
          if (!isStillSignedIn(fbUser) && auth.currentUser) return
          setUser(null)
        }
      } else {
        setUser(null)
      }

      setIsLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      // Resolve only once we know the account is actually an admin,
      // so LoginPage doesn't navigate away before this is known.
      // onAuthStateChanged will also fire in the background and
      // set the same state via fetchAdminProfile — harmless, just
      // one redundant GET /api/auth/me.
      const dbUser = await fetchAdminProfile(cred.user)
      if (isStillSignedIn(cred.user)) setUser(dbUser)
    } catch (err: any) {
      if (err?.code === MFA_REQUIRED_CODE) {
        // Password was correct — Firebase is asking for the second
        // factor before it will finish signing in. Nothing is signed
        // in yet at this point (auth.currentUser is still null), so
        // LoginPage must show the code form and call
        // completeMfaSignIn — this function's job here is just to
        // stash the resolver and let the caller know to do that.
        const resolver = getMultiFactorResolver(auth, err as MultiFactorError)
        setMfaResolver(resolver)
        throw new AdminLoginError('mfa-required', 'Enter the code from your authenticator app.')
      }
      throw err
    }
  }

  const completeMfaSignIn = async (code: string) => {
    if (!mfaResolver) throw new AdminLoginError('no-mfa-challenge', 'No sign-in is waiting for a code.')
    const totpHint = mfaResolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID)
    if (!totpHint) throw new AdminLoginError('no-totp-factor', 'This account has no authenticator app enrolled.')

    const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim())
    // Firebase rejects a wrong/expired code with auth/invalid-verification-code
    // — that throws out of resolveSignIn below and propagates up to
    // LoginPage's catch as-is, before mfaResolver is ever cleared, so the
    // resolver stays valid and the admin can just retype and try again
    // without restarting the whole email/password step.
    const userCred = await mfaResolver.resolveSignIn(assertion)
    // FIX: resolveSignIn succeeding means Firebase is done — the code
    // was correct and this sign-in is no longer "pending a second
    // factor" whatever happens next. Previously mfaResolver was only
    // cleared AFTER fetchAdminProfile also succeeded, so if the code
    // was right but the account turned out to be non-admin (or /me
    // failed), fetchAdminProfile's own firebaseSignOut fired — signing
    // the browser back out — while mfaResolver (and therefore
    // mfaRequired) stayed set. LoginPage has no path back to the plain
    // login form from there except a hard refresh: handleMfaSubmit's
    // catch only special-cases auth/invalid-verification-code and
    // never calls cancelMfaSignIn. Clearing it here, before the
    // profile fetch, means any failure after this point behaves like
    // an ordinary failed sign-in instead of stranding the admin on the
    // code screen.
    setMfaResolver(null)
    const dbUser = await fetchAdminProfile(userCred.user)
    if (isStillSignedIn(userCred.user)) setUser(dbUser)
  }

  const cancelMfaSignIn = () => {
    setMfaResolver(null)
  }

  const logout = async () => {
    pendingTotpSecretRef = null
    await firebaseSignOut(auth)
    setUser(null)
    setMfaResolver(null)
  }

  // FIX: this used to hardcode its own copy of the admin-role list
  // instead of reusing the module-level ADMIN_ROLES constant fetchAdminProfile
  // already uses — the exact drifted-duplicate pattern this codebase's
  // own auth.service.js comments warn recurred three times there.
  const isAdmin = !!user && user.roles.some(r => ADMIN_ROLES.includes(r))

  const mfaEnrolled = !!firebaseUser && multiFactor(firebaseUser).enrolledFactors.some(
    (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
  )

  // Step 1 of enrollment — generates a fresh TOTP secret bound to a
  // short-lived multi-factor session for the CURRENTLY signed-in
  // user, and returns everything SettingsPage needs to show a QR
  // code plus a manual-entry fallback. Firebase requires a RECENT
  // sign-in for this (auth/requires-recent-login otherwise) — that
  // error is left to propagate so SettingsPage can prompt for the
  // password the same way it already does for a password change.
  const beginTotpEnrollment = async (): Promise<{ qrCodeUrl: string; secretKey: string }> => {
    if (!auth.currentUser) throw new AdminLoginError('not-signed-in', 'Not signed in.')
    const session = await multiFactor(auth.currentUser).getSession()
    const totpSecret = await TotpMultiFactorGenerator.generateSecret(session)
    // Kept only in this closure via the qrCodeUrl/secretKey it
    // returns — never sent anywhere, including our own backend. The
    // secret itself must stay between Firebase and the admin's own
    // authenticator app.
    const qrCodeUrl = totpSecret.generateQrCodeUrl(
      auth.currentUser.email || 'admin',
      'MatchNBook Admin'
    )
    // Stash the TotpSecret object itself for completeTotpEnrollment —
    // module-level ref rather than state, since it's never rendered
    // and React state would just be an unnecessary re-render trigger.
    pendingTotpSecretRef = totpSecret
    return { qrCodeUrl, secretKey: totpSecret.secretKey }
  }

  // Step 2 — the admin has scanned the QR (or typed secretKey
  // manually) and entered the 6-digit code their app is now
  // generating. A wrong/expired code throws Firebase's own
  // auth/invalid-verification-code, which SettingsPage surfaces
  // directly rather than needing its own message for it.
  const completeTotpEnrollment = async (code: string) => {
    if (!auth.currentUser) throw new AdminLoginError('not-signed-in', 'Not signed in.')
    if (!pendingTotpSecretRef) throw new AdminLoginError('no-pending-enrollment', 'Start setup again before entering a code.')
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(pendingTotpSecretRef, code.trim())
    await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator app')
    pendingTotpSecretRef = null
    // Firebase's own currentUser object now carries the new factor,
    // but this context's firebaseUser state was set from an earlier
    // snapshot — force a refresh so mfaEnrolled flips immediately
    // instead of waiting for some unrelated auth-state change.
    await auth.currentUser.reload()
    // reload() updates the SAME user object in place, so
    // setFirebaseUser(auth.currentUser) alone is a no-op for React
    // (identical reference → no re-render). Bumping the version forces
    // the re-render that recomputes mfaEnrolled.
    setFirebaseUser(auth.currentUser)
    setMfaStateVersion((v) => v + 1)
  }

  return (
    <AuthContext.Provider value={{
      firebaseUser, user, isLoading, isAdmin, login, logout,
      mfaRequired: !!mfaResolver, completeMfaSignIn, cancelMfaSignIn,
      mfaEnrolled, beginTotpEnrollment, completeTotpEnrollment,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// Module-level, not component state — see beginTotpEnrollment's
// comment above. Cleared on successful enrollment; a stale value
// left over from an abandoned attempt is harmless (completeTotpEnrollment
// would just fail against a secret that was never actually enrolled,
// same as any other wrong code).
let pendingTotpSecretRef: TotpSecret | null = null